import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';
import {
  WearableProvider,
  WearableConnection,
  WearableDataPoint,
  WearableDailySummary,
} from '../types';

const wearableService = {
  // ============================================
  // Providers
  // ============================================

  /**
   * Providers connectable from a browser.
   *
   * `platform=web` excludes Apple Health, Health Connect, Samsung Health,
   * Google Fit and Wear OS. Those are on-device OS APIs with no browser
   * equivalent — Terra authorises them through its mobile SDK, so a Connect
   * button here could never complete. They remain available in the mobile app.
   */
  getProviders: async (): Promise<WearableProvider[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.wearables.providers, {
      params: { platform: 'web' },
    });
    return response.data.results || response.data;
  },

  // ============================================
  // Connections
  // ============================================

  getConnections: async (): Promise<WearableConnection[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.wearables.connections);
    return response.data.results || response.data;
  },

  connectProvider: async (providerSlug: string): Promise<{ auth_url: string }> => {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.wearables.connect}${providerSlug}/`
    );
    return response.data;
  },

  disconnectProvider: async (connectionId: number): Promise<void> => {
    await axiosInstance.post(
      `${API_ENDPOINTS.wearables.connections}${connectionId}/disconnect/`
    );
  },

  triggerSync: async (connectionId: number): Promise<void> => {
    await axiosInstance.post(
      `${API_ENDPOINTS.wearables.connections}${connectionId}/sync/`
    );
  },

  // ============================================
  // Data Points
  // ============================================

  getLatestDataPoints: async (): Promise<WearableDataPoint[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.wearables.dataLatest);
    return response.data.results || response.data;
  },

  getDataPoints: async (params?: {
    metric_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<WearableDataPoint[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.wearables.data, { params });
    return response.data.results || response.data;
  },

  // ============================================
  // Daily Summaries
  // ============================================

  getDailySummaries: async (days?: number): Promise<WearableDailySummary[]> => {
    const params = days ? { days } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.wearables.dailySummaries, { params });
    return response.data.results || response.data;
  },
};

export default wearableService;
